const { useCallback, useEffect, useRef, useState } = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { connect } = require('react-redux');
const { push: Push, replace: Replace } = require('connected-react-router');
const { context } = require('app-context');

const { default: Box } = require('@mui/material/Box');
const { default: Typography } = require('@mui/material/Typography');
const { default: Avatar } = require('@mui/material/Avatar');
const { default: Button } = require('@mui/material/Button');
const { default: Chip } = require('@mui/material/Chip');
const { default: Divider } = require('@mui/material/Divider');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: Tooltip } = require('@mui/material/Tooltip');
const { default: Accordion } = require('@mui/material/Accordion');
const { default: AccordionSummary } = require('@mui/material/AccordionSummary');
const { default: AccordionDetails } = require('@mui/material/AccordionDetails');
const { default: Alert } = require('@mui/material/Alert');
const { default: ChevronLeftIcon } = require('@mui/icons-material/ChevronLeft');
const { default: ChevronRightIcon } = require('@mui/icons-material/ChevronRight');
const { default: ContentCopyIcon } = require('@mui/icons-material/ContentCopy');
const { default: ExpandMoreIcon } = require('@mui/icons-material/ExpandMore');
const { default: OpenInNewIcon } = require('@mui/icons-material/OpenInNew');
const { getDisplayImageUrl: getResourceMediaUrl } = require('utils/image');
const InternalAppLink = require('utils/internalAppLink');
const ScrollPortal = require('components/ScrollPortal');
const YouTubePlayer = require('components/YouTubePlayer');

const selectors = context.selectors.all;
const actions = context.actions;
const redux = context.redux.hooks;
const api = context.api.nearpeer;

const internals = {};

const ResourceDetailPage = function ResourceDetailPage(props) {

    const { resource, resources, isLoadingResources, history, location, onNavigateToResource, onNavigateInApp } = props;

    const isFromList = new URLSearchParams(location?.search).get('fromList') === 'true';
    const currentIndex = isFromList && resources ? resources.findIndex((item) => item.id === resource?.id) : -1;
    const showNavigation = isFromList && resources?.length > 1 && currentIndex >= 0;
    const prevResource = showNavigation ? resources[(currentIndex - 1 + resources.length) % resources.length] : null;
    const nextResource = showNavigation ? resources[(currentIndex + 1) % resources.length] : null;

    const getMediaSrc = (url) => getResourceMediaUrl(url) || null;
    const heroImageSrc = getMediaSrc(resource?.heroImageUrl);
    const heroVideoSrc = getMediaSrc(resource?.heroVideoUrl);
    const youtubeEmbedUrl = YouTubePlayer.getEmbedUrl(resource?.heroVideoUrl);
    const isHeroVideo = Boolean(youtubeEmbedUrl || (resource?.mediaType === 'video' && resource?.heroVideoUrl));
    const [loadedYoutubeResourceKeys, setLoadedYoutubeResourceKeys] = useState(() => new Set());
    const currentYoutubeResourceKey = youtubeEmbedUrl ? `${resource.id}:${resource.heroVideoUrl}` : null;
    const canPreloadAdjacentYoutubeResources = !currentYoutubeResourceKey || loadedYoutubeResourceKeys.has(currentYoutubeResourceKey);
    const youtubeResourcesToMount = [
        resource,
        ...(showNavigation && canPreloadAdjacentYoutubeResources ? [prevResource, nextResource] : [])
    ].filter((candidate, index, candidates) => (
        candidate &&
        YouTubePlayer.getEmbedUrl(candidate.heroVideoUrl) &&
        candidates.findIndex((item) => item?.id === candidate.id) === index
    ));
    const [heroImageLayout, setHeroImageLayout] = useState({ src: null, aspectRatio: null });
    const heroImageAspectRatio = heroImageLayout.src === heroImageSrc ? heroImageLayout.aspectRatio : null;
    const [ctaError, setCtaError] = useState(null);
    const [openingGroup, setOpeningGroup] = useState(false);
    const openingGroupRef = useRef(false);

    useEffect(() => {

        setCtaError(null);
    }, [resource?.id]);

    const handleHeroImageLoad = useCallback((event) => {

        const { naturalWidth, naturalHeight } = event.currentTarget;
        if (!naturalWidth || !naturalHeight) {
            return;
        }

        const aspectRatio = naturalWidth / naturalHeight;
        setHeroImageLayout({
            src: heroImageSrc,
            aspectRatio: aspectRatio > 16 / 9 ? `${naturalWidth} / ${naturalHeight}` : null
        });
    }, [heroImageSrc]);

    const handleYoutubeResourceLoad = useCallback((youtubeResource) => {

        const key = `${youtubeResource.id}:${youtubeResource.heroVideoUrl}`;

        setLoadedYoutubeResourceKeys((loadedKeys) => {

            if (loadedKeys.has(key)) {
                return loadedKeys;
            }

            return new Set([...loadedKeys, key]);
        });
    }, []);

    const now = new Date();
    const resourcesLoaded = !isLoadingResources;
    const isNotTargeted = resourcesLoaded && !!resource && !resources?.some((item) => item.id === resource.id);
    const isUnavailable = !resource ||
        resource.status === 'disabled' ||
        (resource.expirationDate && new Date(resource.expirationDate) < now) ||
        (resource.endDate && new Date(resource.endDate) < now) ||
        isNotTargeted;

    if (isUnavailable) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <Typography color='text.secondary'>Resource not found or has expired.</Typography>
                <Button variant='text' onClick={() => history.push('/app/resources')}>Back to resources</Button>
            </Box>
        );
    }

    const {
        BrandBar,
        HeroMedia,
        CouponBox,
        CtaBar,
        CtaButton,
        NavButton,
        YouTubeLayer
    } = internals;

    const expiresText = resource.expirationDate
        ? `Expires ${new Date(resource.expirationDate).toLocaleDateString()}`
        : null;

    const internalPath = InternalAppLink.getInternalAppPath(resource.ctaDeepLink)
        || InternalAppLink.getInternalAppPath(resource.ctaUrl);

    const handleCta = async (source) => {

        if (openingGroupRef.current) {
            return;
        }

        // Analytics failure should not prevent opening a resource.
        Promise.resolve(api.resources.recordClick(resource.id, source)).catch(() => {});

        if (InternalAppLink.isGroupAppPath(internalPath) || InternalAppLink.isProfileAppPath(internalPath)) {
            openingGroupRef.current = true;
            setOpeningGroup(true);
            setCtaError(null);

            try {
                const { path } = await api.resources.activateGroupLink(resource.id);

                if (InternalAppLink.isGroupChatAppPath(path)) {
                    const syncResult = await redux.dispatch(actions.dataFetching.fetchSync());

                    if (!syncResult) {
                        throw new Error('Unable to refresh group access. Please try again.');
                    }
                }

                onNavigateInApp(path);
            }
            catch (err) {
                setCtaError(err?.response?.data?.message || err?.message || 'Unable to open this group right now. Please try again.');
            }
            finally {
                openingGroupRef.current = false;
                setOpeningGroup(false);
            }

            return;
        }

        if (internalPath) {
            onNavigateInApp(internalPath);
            return;
        }

        internals.openExternalResourceUrl(resource.ctaUrl);
    };

    const handleCopy = () => {

        navigator.clipboard?.writeText(resource.couponCode);
        redux.dispatch(actions.alerts.notification.push({ message: 'Copied to clipboard' }));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {showNavigation && (
                <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, pt: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Tooltip title={prevResource.brandName} placement='bottom' arrow>
                        <NavButton
                            size='small'
                            variant='outlined'
                            startIcon={<ChevronLeftIcon />}
                            onClick={() => onNavigateToResource(prevResource.id)}
                        >
                            Prev
                        </NavButton>
                    </Tooltip>
                    <Typography
                        variant='body1'
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            fontWeight: 600,
                            userSelect: 'none',
                            textAlign: 'center',
                            pointerEvents: 'none'
                        }}
                    >
                        Resource #{currentIndex + 1} of {resources.length}
                    </Typography>
                    <Tooltip title={nextResource.brandName} placement='bottom' arrow>
                        <NavButton
                            size='small'
                            variant='outlined'
                            endIcon={<ChevronRightIcon />}
                            onClick={() => onNavigateToResource(nextResource.id)}
                        >
                            Next
                        </NavButton>
                    </Tooltip>
                </Box>
            )}

            <Box sx={{ pb: 2 }}>
                <BrandBar>
                    <Avatar
                        src={getMediaSrc(resource.brandLogoUrl)}
                        alt={resource.brandName}
                        variant='rounded'
                        sx={{
                            width: 56,
                            height: 56,
                            mr: 2,
                            flexShrink: 0,
                            bgcolor: 'background.paper',
                            '& img': {
                                objectFit: 'contain'
                            }
                        }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                            {resource.brandName}
                        </Typography>
                        {resource.tagText && (
                            <Chip
                                label={resource.tagText}
                                size='small'
                                onClick={() => handleCta('app_tag')}
                                sx={{
                                    mt: 0.5,
                                    display: 'inline-flex',
                                    width: 'fit-content',
                                    maxWidth: '100%',
                                    height: 'auto',
                                    alignItems: 'center',
                                    backgroundColor: resource.tagColor || undefined,
                                    color: resource.tagTextColor || (resource.tagColor ? '#fff' : undefined),
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: resource.tagColor || undefined,
                                        opacity: 0.85
                                    },
                                    '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        py: 0.25,
                                        textAlign: 'center'
                                    }
                                }}
                            />
                        )}
                    </Box>
                </BrandBar>

                <Box sx={{ px: 2, pt: 2 }}>
                    <Typography variant='h5' sx={{ fontWeight: 700, lineHeight: 1.3, mb: 1 }}>
                        {resource.title}
                    </Typography>
                </Box>

                {(resource.heroImageUrl || resource.heroVideoUrl) && (
                    <>
                        <HeroMedia
                            onClick={youtubeEmbedUrl ? undefined : () => handleCta('app_hero')}
                            $aspectRatio={!isHeroVideo ? heroImageAspectRatio : null}
                        >
                            {youtubeResourcesToMount.map((youtubeResource) => {

                                const isActive = youtubeResource.id === resource.id;

                                return (
                                    <YouTubeLayer
                                        key={youtubeResource.id}
                                        $active={isActive}
                                    >
                                        <YouTubePlayer
                                            url={youtubeResource.heroVideoUrl}
                                            title={youtubeResource.title}
                                            active={isActive}
                                            playerId={`nearpeer-youtube-player-${youtubeResource.id}`}
                                            onLoad={() => handleYoutubeResourceLoad(youtubeResource)}
                                        />
                                    </YouTubeLayer>
                                );
                            })}
                            {!youtubeEmbedUrl && (() => {

                                if (resource.mediaType === 'video' && resource.heroVideoUrl) {
                                    return (
                                        <video
                                            key={resource.id}
                                            src={heroVideoSrc}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    );
                                }

                                return (
                                    <img
                                        key={resource.id}
                                        src={heroImageSrc}
                                        alt={resource.title}
                                        onLoad={handleHeroImageLoad}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'transparent' }}
                                    />
                                );
                            })()}
                        </HeroMedia>
                    </>
                )}

                <Box sx={{ px: 2, pt: 2 }}>
                    {resource.subtitle && (
                        <Typography variant='body1' sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                            {resource.subtitle}
                        </Typography>
                    )}

                    {resource.couponCode && (
                        <CouponBox>
                            <Typography variant='body2' sx={{ mr: 1, color: 'text.secondary' }}>
                                Coupon code:
                            </Typography>
                            <Typography variant='subtitle1' sx={{ fontWeight: 700, letterSpacing: 2, flex: 1 }}>
                                {resource.couponCode}
                            </Typography>
                            <IconButton size='small' onClick={handleCopy} aria-label='Copy coupon code'>
                                <ContentCopyIcon fontSize='small' />
                            </IconButton>
                        </CouponBox>
                    )}

                    {expiresText && (
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 2 }}>
                            {expiresText}
                        </Typography>
                    )}

                    {resource.terms && (
                        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant='body2' sx={{ fontWeight: 600 }}>Terms &amp; Conditions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'pre-wrap' }}>
                                    {resource.terms}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
            </Box>

            <ScrollPortal bottom>
                <CtaBar>
                    {ctaError && <Alert severity='error'>{ctaError}</Alert>}
                    <Divider />
                    <Box sx={{ p: 2 }}>
                        <CtaButton
                            disabled={openingGroup}
                            variant='contained'
                            fullWidth
                            size='large'
                            endIcon={internalPath ? undefined : <OpenInNewIcon />}
                            onClick={() => handleCta('app_cta')}
                            sx={{
                                bgcolor: (theme) => resource.ctaBgColor || theme.palette.primary.main,
                                color: resource.ctaTextColor || '#000000',
                                '&:hover': {
                                    bgcolor: (theme) => resource.ctaBgColor || theme.palette.primary.main
                                }
                            }}
                        >
                            {resource.ctaText}
                        </CtaButton>
                    </Box>
                </CtaBar>
            </ScrollPortal>
        </Box>
    );
};

ResourceDetailPage.propTypes = {
    resource: T.object,
    resources: T.array,
    isLoadingResources: T.bool,
    history: T.object,
    location: T.object,
    onNavigateToResource: T.func,
    onNavigateInApp: T.func
};

internals.BrandBar = Styled(Box)`
    display: flex;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

internals.HeroMedia = Styled(Box)`
    margin: 16px;
    border-radius: 12px;
    overflow: hidden;
    aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || '16 / 9'};
    background-color: transparent;
    cursor: pointer;
    transition: opacity 0.2s ease;
    position: relative;

    &:hover {
        opacity: 0.9;
    }
`;

internals.YouTubeLayer = Styled.div`
    position: absolute;
    inset: 0;
    z-index: ${({ $active }) => $active ? 1 : 0};
    visibility: ${({ $active }) => $active ? 'visible' : 'hidden'};
    pointer-events: ${({ $active }) => $active ? 'auto' : 'none'};
`;

internals.CouponBox = Styled(Box)`
    display: flex;
    align-items: center;
    border: 2px dashed ${({ theme }) => theme.palette.primary.main};
    border-radius: 8px;
    padding: 8px 12px;
    margin-bottom: 12px;
    background-color: ${({ theme }) => theme.palette.primary.main}11;
`;

internals.CtaBar = Styled(Box)`
    width: 100%;
    background-color: ${({ theme }) => theme.palette.background.paper};
`;

internals.CtaButton = Styled(Button)`
    border-radius: 24px !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px !important;
    transition: none !important;
`;

internals.NavButton = Styled(Button)`
    min-width: 0 !important;
    flex-shrink: 1;
    border-radius: 16px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    border-color: ${({ theme }) => theme.palette.primary.main}80 !important;
    background-color: ${({ theme }) => theme.palette.primary.main}0F !important;

    &:hover {
        border-color: ${({ theme }) => theme.palette.primary.main} !important;
        background-color: ${({ theme }) => theme.palette.primary.main}1F !important;
    }

    & .MuiButton-startIcon,
    & .MuiButton-endIcon {
        flex-shrink: 0;
    }
`;

const ConnectedResourceDetailPage = connect(
    (state, ownProps) => {

        const resourceId = Number(ownProps.match?.params?.id);

        return {
            resource: selectors.getResource(state, resourceId),
            resources: selectors.getResources(state),
            isLoadingResources: selectors.getIsLoadingResources(state)
        };
    },
    (dispatch) => ({
        onNavigateToResource: (resourceId) => dispatch(Replace(`/app/resources/${resourceId}?fromList=true`)),
        onNavigateInApp: (path) => dispatch(Push(path))
    })
)(ResourceDetailPage);

internals.openExternalResourceUrl = InternalAppLink.openExternalUrl;

module.exports = ConnectedResourceDetailPage;
module.exports.Component = ResourceDetailPage;
module.exports.internals = internals;
